import { Grid2, Typography } from '@mui/material';
import TextFieldComponent from '../../../components/TextFieldComponent';
import ChipListComponent from '../../../components/ChipListComponent';
import ButtonComponent from '../../../components/ButtonComponent';
import { Check, DirectionsCar } from '@mui/icons-material';
import * as React from 'react';
import { FormEvent, useContext, useEffect, useState } from 'react';
import zod from 'zod';
import { LprGroupFormSchema } from '../../../services/LprGroupService/schemas';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import ModalComponent from '../../../components/ModalComponent';
import CheckboxComponent from '../../../components/CheckboxComponent';
import {
  useCreateLprGroupAsync,
  useUpdateLprGroupAsync,
} from '../../../services/LprGroupService/hooks/useMutation';
import { AppContext } from '../../../context/AppContextProvider';
import {
  ILprGroupProps,
  ILprGroupSimpleProps,
} from '../../../services/LprGroupService/types';

interface LprGroupEditionProps {
  isOpen: boolean;
  onClose: (changedGroup?: any) => void;
  selectedGroupToEdit?: ILprGroupProps | ILprGroupSimpleProps;
}

type LprGroupFormInput = zod.infer<typeof LprGroupFormSchema>;

const LprGroupEditionModal = ({
  isOpen,
  onClose,
  selectedGroupToEdit,
}: LprGroupEditionProps) => {
  const [plate, setPlate] = useState('');
  const { showSnackbar } = useContext(AppContext);
  const { control, getValues, handleSubmit, setValue, watch, reset } =
    useForm<LprGroupFormInput>({
      resolver: zodResolver(LprGroupFormSchema),
      defaultValues: {
        description: '',
        name: '',
        plates: [],
        isSendAllEvents: false,
      },
    });

  const {
    mutateAsync: mutateCreateLprGroupAsync,
    isLoading: isLoadingCreateLprGroupAsync,
  } = useCreateLprGroupAsync();

  const {
    mutateAsync: mutateUpdateLprGroupAsync,
    isLoading: isLoadingUpdateLprGroupAsync,
  } = useUpdateLprGroupAsync();

  const onSubmitForm = (data: LprGroupFormInput) => {
    if (data.id) {
      mutateUpdateLprGroupAsync({ id: data.id, data }).then(() => {
        showSnackbar('Grupo de placas atualizado com sucesso!', 'success');
        reset();
        onClose(data);
      });
      return;
    }

    mutateCreateLprGroupAsync(data).then(() => {
      showSnackbar('Grupo de placas criado com sucesso!', 'success');
      reset();
      onClose();
    });
  };

  const platesWatch = watch('plates');

  const onSubmitPlate = (event: FormEvent) => {
    event.preventDefault();
    setValue('plates', [...getValues('plates'), plate]);
    setPlate('');
  };

  useEffect(() => {
    if (selectedGroupToEdit) {
      reset(selectedGroupToEdit, { keepDefaultValues: true });
    } else reset();
  }, [selectedGroupToEdit]);

  return (
    <ModalComponent
      onClose={onClose}
      isOpen={isOpen}
      title={'Edição de grupo de placas'}
    >
      <Grid2 container>
        <Grid2 size={12}>
          <Controller
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <TextFieldComponent
                label={'Nome'}
                errorMessage={error?.message}
                value={value}
                onChangeText={onChange}
              />
            )}
            name={'name'}
            control={control}
          />

          <>
            <Grid2 size={12} marginBottom={3}>
              <form style={{ display: 'flex' }} onSubmit={onSubmitPlate}>
                <TextFieldComponent
                  value={plate}
                  mb={0}
                  onChangeText={setPlate}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderBottomRightRadius: 0,
                        borderTopRightRadius: 0,
                      },
                    },
                  }}
                  label={'Placa'}
                />
                <ButtonComponent
                  type="submit"
                  sx={{
                    borderBottomLeftRadius: 0,
                    borderTopLeftRadius: 0,
                    height: '100%',
                  }}
                >
                  <Check />
                </ButtonComponent>
              </form>
              <Typography variant="caption" gutterBottom>
                Pressione ENTER para inserir a placa
              </Typography>
            </Grid2>

            {platesWatch && platesWatch.length > 0 && (
              <Grid2 size={12} sx={{ mb: 4 }}>
                <ChipListComponent
                  chipIcon={<DirectionsCar />}
                  chipData={platesWatch}
                  onDelete={(index) => {
                    const newVal = platesWatch.filter((_, i) => i !== index);
                    setValue('plates', newVal);
                  }}
                />
              </Grid2>
            )}

            <Grid2 size={12}>
              <Controller
                render={({ field: { value, onChange } }) => (
                  <TextFieldComponent
                    onChangeText={onChange}
                    value={value}
                    label={'Descrição'}
                  />
                )}
                name={'description'}
                control={control}
              />
            </Grid2>

            <Grid2 size={12}>
              <Controller
                render={({ field: { value, onChange } }) => (
                  <CheckboxComponent
                    value={value}
                    label={'Notificar quando reconhecidas pelos dispositivos'}
                    onChange={onChange}
                  />
                )}
                name={'isSendAllEvents'}
                control={control}
              />
            </Grid2>
          </>
        </Grid2>

        <Grid2 size={12} sx={{ mt: 1 }} container columnSpacing={2}>
          <Grid2 size={{ md: 6, xs: 12 }}>
            <ButtonComponent
              fullWidth
              onClick={handleSubmit(onSubmitForm)}
              loading={
                isLoadingUpdateLprGroupAsync || isLoadingCreateLprGroupAsync
              }
            >
              Salvar
            </ButtonComponent>
          </Grid2>
          <Grid2 size={{ md: 6, xs: 12 }}>
            <ButtonComponent
              fullWidth
              color={'error'}
              onClick={() => onClose()}
            >
              Cancelar
            </ButtonComponent>
          </Grid2>
        </Grid2>
      </Grid2>
    </ModalComponent>
  );
};

export default LprGroupEditionModal;
